import { coverDoneText, coverIsActive, type CoverRequest, type CoverRequestsResponse, type CoverWords } from "@teamna/shared";
import { useState } from "react";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { useCoverAction, useCovers, type CoverAction } from "@teamna/web-client/hooks/use-covers.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { Button, SkeletonList, showToast } from "../components/index.ts";
import { CoverCard, type CoverActionKind } from "./CoverCard.tsx";

/**
 * Cover requests between staff on Desk (Pilates studio shape S3, migration 0072), connected to the
 * API: the owner's session panel, the owner's To do row "Cover requests", and the staff Today
 * screen. The words are the business's own labels; the sentences come from `coverText` in shared,
 * the same as the mobile app.
 */

/** "instructor" and "instructors", from the active business (owner screens). */
export function useCoverWords(): CoverWords {
  const { label } = useLabels();
  return { staff: label("staff"), staffPlural: label("staff", { plural: true }) };
}

/** "Someone took it. Another person took this cover first." */
export function coverErrorText(error: unknown): string {
  const copy = copyForError(error);
  return `${copy.title}. ${copy.message}`;
}

/** One action at a time; the toast says what happened. */
export function useCoverRun(businessId: string) {
  const action = useCoverAction(businessId);
  const [busy, setBusy] = useState<{ id: string; kind: CoverActionKind | "ask" } | null>(null);
  function run(kind: CoverActionKind, cover: CoverRequest): void;
  function run(kind: "ask", sessionId: string): void;
  function run(kind: CoverActionKind | "ask", target: CoverRequest | string): void {
    const id = typeof target === "string" ? target : target.id;
    const request: CoverAction =
      kind === "ask"
        ? { kind, sessionId: id }
        : kind === "approve" || kind === "refuse"
          ? { kind: "decide", coverId: id, decision: kind }
          : { kind, coverId: id };
    setBusy({ id, kind });
    action.mutate(request, {
      onSuccess: (list: CoverRequestsResponse) => showToast(coverDoneText(kind, list.items.find((row) => row.id === id))),
      onError: (error) => showToast(coverErrorText(error), { tone: "info" }),
      onSettled: () => setBusy(null),
    });
  }
  return { run, busy };
}

/** A list of cover cards with their buttons. */
export function CoverList({
  businessId,
  items,
  words,
  timezone,
  label,
}: {
  businessId: string;
  items: CoverRequest[];
  words: CoverWords;
  timezone?: string;
  label: string;
}) {
  const { run, busy } = useCoverRun(businessId);
  return (
    <ul aria-label={label} className="flex flex-col gap-2">
      {items.map((cover) => (
        <li key={cover.id}>
          <CoverCard
            cover={cover}
            words={words}
            timezone={timezone}
            busy={busy?.id === cover.id && busy.kind !== "ask" ? busy.kind : null}
            onAction={(kind) => run(kind, cover)}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * The cover of one session, in the owner's session panel (desktop and phone): the request that
 * waits, or the last answer. Nothing when the session has no request, or the read failed; the
 * session panel works without it.
 */
export function SessionCover({ businessId, sessionId, timezone }: { businessId: string; sessionId: string; timezone?: string }) {
  const covers = useCovers(businessId);
  const words = useCoverWords();
  const { run, busy } = useCoverRun(businessId);
  const cover = covers.data?.items.find((row) => row.sessionId === sessionId);
  if (!cover) return null;
  return (
    <section aria-label="Cover" className="grid gap-1.5">
      <p className="eyebrow text-ink-2">Cover</p>
      <CoverCard
        cover={cover}
        words={words}
        timezone={timezone}
        showSession={false}
        busy={busy?.id === cover.id && busy.kind !== "ask" ? busy.kind : null}
        onAction={(kind) => run(kind, cover)}
      />
    </section>
  );
}

/** The To do row "Cover requests" (Approve each cover on): each yes that waits for the owner. */
export function CoverApprovalsPanel({ businessId, timezone }: { businessId: string; timezone: string }) {
  const covers = useCovers(businessId);
  const words = useCoverWords();
  if (!covers.data) {
    if (covers.isLoading) return <SkeletonList rows={1} />;
    return (
      <div className="flex flex-wrap items-center gap-2 text-[13.5px] text-ink-2">
        {coverErrorText(covers.error)}
        <Button size="sm" variant="ghost" onClick={() => void covers.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  const waiting = covers.data.items.filter((row) => row.status === "awaiting_approval" && row.canDecide);
  if (waiting.length === 0) return <p className="text-[13.5px] text-ink-2">No cover waits for you.</p>;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13.5px] text-ink-2">
        Approve each cover is on: a {words.staff} who said yes runs the session only after you approve. Refuse opens the request again for the others.
      </p>
      <CoverList businessId={businessId} items={waiting} words={words} timezone={timezone} label="Covers to approve" />
    </div>
  );
}

/**
 * "Ask for cover", on a session the reader runs. Hidden while the session has a request that waits:
 * its card shows instead.
 */
export function AskCoverButton({
  businessId,
  sessionId,
  covers,
}: {
  businessId: string;
  sessionId: string;
  covers: CoverRequest[];
}) {
  const { run, busy } = useCoverRun(businessId);
  if (covers.some((row) => row.sessionId === sessionId && coverIsActive(row))) return null;
  return (
    <Button variant="secondary" size="sm" loading={busy?.id === sessionId} onClick={() => run("ask", sessionId)}>
      Ask for cover
    </Button>
  );
}
